import { UserSelectors } from '@next/core-lib/user';
import { Store } from '@ngrx/store';
import { combineLatest, distinctUntilChanged, filter, map, Observable } from 'rxjs';

/**
 * Represents a transition in the selected customer context.
 *
 * `previousCustomerId` can be `null` when a customer becomes available after no selection.
 */
export interface CustomerSwitch {
  previousCustomerId: number | null;
  currentCustomerId: number;
}

/**
 * Emits a `CustomerSwitch` whenever the selected customer id changes to a non-null value.
 *
 * Notes:
 * - Emits `null -> id` when a customer is selected after no customer.
 * - Emits `idA -> idB` for real customer switches.
 * - Does not emit when the id remains unchanged.
 *
 * Consumers can decide whether to handle initial `null -> id` transitions or only real switches.
 */
export const observeCustomerSwitches = (store: Store): Observable<CustomerSwitch> =>
  combineLatest([
    store.select(UserSelectors.selectPreviousCustomerId).pipe(
      map((previousCustomerId) => previousCustomerId ?? null),
      distinctUntilChanged(),
    ),
    store.select(UserSelectors.selectCustomer).pipe(
      map((customer) => customer?.id ?? null),
      distinctUntilChanged(),
    ),
  ]).pipe(
    filter(
      (customerIds): customerIds is [number | null, number] =>
        customerIds[1] !== null && customerIds[0] !== customerIds[1],
    ),
    map(([previousCustomerId, currentCustomerId]) => ({
      previousCustomerId: previousCustomerId ?? null,
      currentCustomerId,
    })),
  );
